import * as microsoftTeams from '@microsoft/teams-js';
import { apiService } from '../api/apiService';

interface TeamsUser {
    id: string;
    displayName: string;
    userPrincipalName: string;
}

interface TeamsMeeting {
    id: string;
    title: string;
}

interface TeamsContext {
    isInTeams: boolean;
    user?: TeamsUser;
    meeting?: TeamsMeeting;
    theme: string;
}

interface CallOptions {
    targets?: string[];
    participants?: string[];
    consultationId?: string;
    patientName?: string;
    interpreterRequired?: boolean;
    language?: string;
    deviceId?: string; // <-- add this
}

interface ConsultationStartOptions {
    consultationId: string;
    patientInfo: {
        name: string;
        language?: string;
    };
    doctorId: string;
    scheduledTime: string;
    estimatedDuration?: number;
    interpreterRequired?: boolean;
    priority?: string;
}

class TeamsService {
    private isInitialized = false;
    private teamsContext: TeamsContext = {
        isInTeams: false,
        theme: 'default',
    };

    async initialize(): Promise<TeamsContext> {
        try {
            console.log('🔄 Initializing Microsoft Teams SDK...');

            // Initialize Teams SDK
            await microsoftTeams.app.initialize();

            console.log('✅ Teams SDK initialized successfully');
            this.isInitialized = true;

            // Get Teams context
            const context = await microsoftTeams.app.getContext();

            this.teamsContext = {
                isInTeams: true,
                user: context.user
                    ? {
                          id: context.user.id,
                          displayName: context.user.displayName || 'Unknown User',
                          userPrincipalName: context.user.userPrincipalName || '',
                      }
                    : undefined,
                meeting: context.meeting
                    ? {
                          id: context.meeting.id,
                          title: 'Meeting', // Teams SDK doesn't provide title directly
                      }
                    : undefined,
                theme: (context.app.theme as string) || 'default',
            };

            console.log('✅ Teams context loaded:', this.teamsContext);

            return this.teamsContext;
        } catch (error) {
            console.warn('⚠️ Teams SDK initialization failed (running in standalone mode):', error);
            this.teamsContext = {
                isInTeams: false,
                theme: 'default',
            };
            return this.teamsContext;
        }
    }

    async createConsultation(
        options: ConsultationStartOptions
    ): Promise<{ success: boolean; consultation?: any; error?: string }> {
        try {
            console.log('🚀 Creating consultation with Teams integration:', options);

            // Create consultation via backend API
            const response = await apiService.createConsultation({
                patientId: options.patientInfo.name, // In real app, this would be proper patient ID
                doctorId: options.doctorId,
                scheduledTime: options.scheduledTime,
                consultationType: 'psychiatric',
                priority: options.priority || 'routine',
                interpreterRequired: options.interpreterRequired || false,
                interpreterLanguage: options.patientInfo.language || 'English',
                estimatedDuration: options.estimatedDuration || 60,
            });

            if (response.success) {
                console.log(
                    '✅ Consultation created with Teams meeting:',
                    response.consultation.teamsMeetingId
                );

                return {
                    success: true,
                    consultation: {
                        id: response.consultation.id,
                        teamsJoinUrl: response.consultation.teamsJoinUrl,
                        teamsMeetingId: response.consultation.teamsMeetingId,
                        conferenceId: response.teamsIntegration?.conferenceId,
                        scheduledTime: options.scheduledTime,
                        patientName: options.patientInfo.name,
                        estimatedDuration: options.estimatedDuration,
                    },
                };
            } else {
                throw new Error(response.error || 'Failed to create consultation');
            }
        } catch (error) {
            console.error('❌ Failed to create consultation:', error);
            return {
                success: false,
                error: error instanceof Error ? error.message : 'Unknown error',
            };
        }
    }

    async startCall(options: CallOptions): Promise<{ success: boolean; error?: string }> {
        try {
            if (!this.isInitialized) {
                await this.initialize();
            }

            console.log('🎥 Starting Teams consultation:', options);

            if (options.consultationId) {
                // Start existing consultation
                const response = await apiService.startConsultation(options.consultationId);

                if (response.success && response.teamsIntegration?.joinUrl) {
                    if (this.teamsContext.isInTeams) {
                        // In Teams - open the meeting URL
                        await this.openTeamsMeeting(response.teamsIntegration.joinUrl);
                    } else {
                        // Standalone mode - open in new window
                        window.open(response.teamsIntegration.joinUrl, '_blank');
                    }

                    console.log('✅ Teams consultation started successfully');
                    return { success: true };
                } else {
                    throw new Error(response.error || 'Failed to start consultation');
                }
            } else {
                // Legacy call handling - redirect to video interface
                if (this.teamsContext.isInTeams) {
                    console.log('📞 Teams call functionality available');
                    window.location.href = '/consult';
                } else {
                    console.log('📞 Simulating call in standalone mode');
                    this.showStandaloneCallNotification(options);
                    window.location.href = '/consult';
                }

                return { success: true };
            }
        } catch (error) {
            console.error('❌ Failed to start call:', error);
            return {
                success: false,
                error: error instanceof Error ? error.message : 'Unknown error',
            };
        }
    }

    async startDeviceCall(options: CallOptions): Promise<{ success: boolean; error?: string }> {
        try {
            if (!this.isInitialized) {
                await this.initialize();
            }

            if (!options.consultationId || !options.deviceId) {
                throw new Error('consultationId and deviceId are required');
            }

            console.log('📱 Starting device-based Teams consultation:', options);
            const response = await apiService.startDeviceConsultation(
                options.consultationId,
                options.deviceId
            );

            if (response.success && response.consultation?.callId) {
                // If there is a join URL or similar, open it
                if (response.consultation.device?.joinUrl) {
                    if (this.teamsContext.isInTeams) {
                        await this.openTeamsMeeting(response.consultation.device.joinUrl);
                    } else {
                        window.open(response.consultation.device.joinUrl, '_blank');
                    }
                }
                console.log('✅ Device-based Teams consultation started successfully');
                return { success: true };
            } else {
                throw new Error(response.error || 'Failed to start device-based consultation');
            }
        } catch (error) {
            console.error('❌ Failed to start device-based call:', error);
            return {
                success: false,
                error: error instanceof Error ? error.message : 'Unknown error',
            };
        }
    }

    async endConsultation(
        consultationId: string,
        notes?: string
    ): Promise<{ success: boolean; error?: string }> {
        try {
            console.log('🔚 Ending consultation:', consultationId);

            const response = await apiService.endConsultation(consultationId, {
                notes,
                summary: 'Consultation completed via Teams',
            });

            if (response.success) {
                console.log('✅ Consultation ended successfully');
                return { success: true };
            } else {
                throw new Error(response.error || 'Failed to end consultation');
            }
        } catch (error) {
            console.error('❌ Failed to end consultation:', error);
            return {
                success: false,
                error: error instanceof Error ? error.message : 'Unknown error',
            };
        }
    }

    async joinMeeting(meetingId: string): Promise<boolean> {
        try {
            if (!this.isInitialized) {
                await this.initialize();
            }

            if (this.teamsContext.isInTeams) {
                console.log('🎥 Joining Teams meeting:', meetingId);

                // If we have a Teams join URL, use it directly
                if (meetingId.startsWith('https://')) {
                    await this.openTeamsMeeting(meetingId);
                } else {
                    // Otherwise navigate to video call interface
                    window.location.href = '/consult';
                }

                console.log('✅ Joined Teams meeting successfully');
                return true;
            } else {
                console.log('🎥 Simulating meeting join in standalone mode');

                // In standalone mode, check if it's a Teams URL
                if (meetingId.startsWith('https://teams.microsoft.com')) {
                    window.open(meetingId, '_blank');
                } else {
                    window.location.href = '/consult';
                }
                return true;
            }
        } catch (error) {
            console.error('❌ Failed to join meeting:', error);
            return false;
        }
    }

    private async openTeamsMeeting(joinUrl: string): Promise<void> {
        try {
            if (this.teamsContext.isInTeams) {
                // Use Teams SDK to open meeting
                await microsoftTeams.app.openLink(joinUrl);
            } else {
                // Fallback to opening in new window
                window.open(joinUrl, '_blank');
            }
        } catch (error) {
            console.error('❌ Failed to open Teams meeting:', error);
            // Fallback
            window.open(joinUrl, '_blank');
        }
    }

    private showStandaloneCallNotification(options: CallOptions): void {
        const message = `Call initiated to: ${(
            options.targets ||
            options.participants || ['Unknown']
        ).join(', ')}`;

        if ('Notification' in window) {
            if (Notification.permission === 'granted') {
                new Notification('Call Started', {
                    body: message,
                    icon: '/logo192.png',
                });
            } else if (Notification.permission !== 'denied') {
                Notification.requestPermission().then((permission) => {
                    if (permission === 'granted') {
                        new Notification('Call Started', {
                            body: message,
                            icon: '/logo192.png',
                        });
                    }
                });
            }
        } else {
            console.log('📞', message);
            alert(message);
        }
    }

    // =================== NOTIFICATION METHODS ===================
    async sendNotification(
        userId: string,
        notification: {
            title: string;
            message: string;
            actionUrl?: string;
        }
    ): Promise<boolean> {
        try {
            if (this.teamsContext.isInTeams) {
                // Use Teams SDK to send notification
                await microsoftTeams.app.notifySuccess();
                console.log(
                    '📢 Teams notification sent:',
                    notification.title,
                    notification.message
                );
                return true;
            } else {
                // Fallback to browser notification
                if ('Notification' in window && Notification.permission === 'granted') {
                    new Notification(notification.title, {
                        body: notification.message,
                        icon: '/logo192.png',
                    });
                    return true;
                }
                return false;
            }
        } catch (error) {
            console.error('❌ Failed to send notification:', error);
            return false;
        }
    }

    // =================== UTILITY METHODS ===================
    getContext(): TeamsContext {
        return this.teamsContext;
    }

    isInTeamsEnvironment(): boolean {
        return this.teamsContext.isInTeams;
    }

    getCurrentUser(): TeamsUser | undefined {
        return this.teamsContext.user;
    }

    getCurrentMeeting(): TeamsMeeting | undefined {
        return this.teamsContext.meeting;
    }

    getTheme(): string {
        return this.teamsContext.theme;
    }

    // =================== CONSULTATION MANAGEMENT ===================
    async getActiveConsultations(): Promise<any[]> {
        try {
            const response = await apiService.getActiveConsultations();
            return response.activeConsultations || [];
        } catch (error) {
            console.error('❌ Failed to get active consultations:', error);
            return [];
        }
    }

    async rescheduleConsultation(
        consultationId: string,
        newStartTime: string,
        reason?: string
    ): Promise<{ success: boolean; error?: string }> {
        try {
            const response = await apiService.rescheduleConsultation(consultationId, {
                newStartTime,
                reason,
            });

            if (response.success) {
                console.log('✅ Consultation rescheduled successfully');
                return { success: true };
            } else {
                throw new Error(response.error || 'Failed to reschedule consultation');
            }
        } catch (error) {
            console.error('❌ Failed to reschedule consultation:', error);
            return {
                success: false,
                error: error instanceof Error ? error.message : 'Unknown error',
            };
        }
    }

    async cancelConsultation(
        consultationId: string,
        reason?: string
    ): Promise<{ success: boolean; error?: string }> {
        try {
            const response = await apiService.cancelConsultation(consultationId, reason);

            if (response.success) {
                console.log('✅ Consultation cancelled successfully');
                return { success: true };
            } else {
                throw new Error(response.error || 'Failed to cancel consultation');
            }
        } catch (error) {
            console.error('❌ Failed to cancel consultation:', error);
            return {
                success: false,
                error: error instanceof Error ? error.message : 'Unknown error',
            };
        }
    }
}

export const teamsService = new TeamsService();
export type { TeamsContext, TeamsUser, TeamsMeeting, CallOptions, ConsultationStartOptions };
